import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken, getToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = still checking, null = logged out, object = logged in
  const [user, setUser] = useState(undefined);
  const [actingAs, setActingAsState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("equimare_act_as")) || null;
    } catch {
      return null;
    }
  });

  const setActingAs = useCallback((val) => {
    if (val) localStorage.setItem("equimare_act_as", JSON.stringify(val));
    else localStorage.removeItem("equimare_act_as");
    setActingAsState(val);
  }, []);

  const [activeFarm, setActiveFarmState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("equimare_farm")) || null;
    } catch {
      return null;
    }
  });

  const [pendingInviteCount, setPendingInviteCount] = useState(0);

  const refreshInvites = useCallback(async () => {
    if (!getToken()) {
      setPendingInviteCount(0);
      return;
    }
    try {
      const { data } = await api.get("/links");
      setPendingInviteCount(data.filter((l) => l.actionable).length);
    } catch {
      /* ignore */
    }
  }, []);

  const setActiveFarm = useCallback((val) => {
    if (val) localStorage.setItem("equimare_farm", JSON.stringify(val));
    else localStorage.removeItem("equimare_farm");
    setActiveFarmState(val);
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      refreshInvites();
    } catch {
      clearToken();
      setUser(null);
    }
  }, [refreshInvites]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    clearToken();
    setActingAs(null);
    setActiveFarm(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, signup, logout, refresh, actingAs, setActingAs, readOnly: !!actingAs, activeFarm, setActiveFarm, isVet: user?.account_type === "vet", pendingInviteCount, refreshInvites }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
